import { UploadDragger } from "@/components/upload-dragger/UploadDragger";
import ContentWrapper from "@/components/wrappers/ContentWrapper";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { App as AntApp, Card, Tabs } from "antd";
import { RcFile } from "antd/lib/upload";
import classNames from "classnames";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useDeleteDocumentNode,
  useDownloadDocumentNode,
  useGetDocumentNodeTree,
  useUploadDocumentNodes,
} from "../../../common/api/queries";
import { Permission } from "../../../common/security/enums";
import { useHasPermissions } from "../../auth/hooks";
import DocumentNodeMoveForm from "../components/forms/DocumentNodeMoveForm";
import DocumentNodeNameModalForm from "../components/forms/DocumentNodeNameModalForm";
import DocumentNodeBreadcrumbsView from "../components/views/DocumentNodeBreadcrumbsView";
import { DocumentNodeListView } from "../components/views/DocumentNodeListView";
import { DocumentNodeToolbarMenuView } from "../components/views/DocumentNodeToolbarMenuView";
import { DocumentNodeType } from "../enums";
import { DocumentNodeTree } from "../types";
import { findNodeByTreePath, getBreadcrumbs, getNodeFoldersTree } from "../utils";
import styles from "./DocumentNodeContainer.module.scss";

export const DocumentNodeContainer = () => {
  const { t } = useTranslation();
  const { modal } = AntApp.useApp();

  const hasAdminDocumentNodesPermission = useHasPermissions(Permission.ADMIN_DOCUMENT_NODES);

  const [currentPath, setCurrentPath] = useState<string>("");
  const [currentNodeType, setCurrentNodeType] = useState(DocumentNodeType.GENERAL);
  const [selectedNodes, setSelectedNodes] = useState<DocumentNodeTree[]>([]);

  const [isOpenRenameModal, setIsOpenRenameModal] = useState(false);
  const [renameNode, setRenameNode] = useState<DocumentNodeTree>();
  const [renameNodeType, setRenameNodeType] = useState<"createFolder" | "renameNode">("createFolder");

  const [isOpenMoveModal, setIsOpenMoveModal] = useState(false);
  const [nodesToMove, setNodesToMove] = useState<DocumentNodeTree[]>();

  const hasActionsPermission = currentNodeType !== DocumentNodeType.GENERAL || hasAdminDocumentNodesPermission;

  const { data: documentTree } = useGetDocumentNodeTree();
  const downloadDocumentNode = useDownloadDocumentNode();
  const uploadDocumentNodes = useUploadDocumentNodes();
  const deleteDocumentNode = useDeleteDocumentNode();

  const fullTree: DocumentNodeTree[] =
    (currentNodeType === DocumentNodeType.PERSONAL ? documentTree?.personalNodes : documentTree?.generalNodes) ?? [];

  const handleTabKeyChange = (type: DocumentNodeType) => {
    setSelectedNodes([]);
    setCurrentNodeType(type);
  };

  const handleCreateFolderClick = () => {
    setIsOpenRenameModal(true);
    setRenameNodeType("createFolder");
  };

  const handleRenameNodeClick = (node: DocumentNodeTree) => {
    setIsOpenRenameModal(true);
    setRenameNodeType("renameNode");
    setRenameNode(node);
  };

  const handleMoveNodesClick = (nodesToMove?: DocumentNodeTree[]) => {
    setIsOpenMoveModal(true);
    setNodesToMove(nodesToMove ?? selectedNodes);
  };

  const handleDeleteNodesClick = (nodesToDelete?: DocumentNodeTree[]) => {
    modal.confirm({
      title: t("documentNode.helpers.deleteConfirmTitle"),
      icon: <ExclamationCircleOutlined />,
      content: t("documentNode.helpers.deleteConfirmDesc", {
        fileNames: (nodesToDelete ?? selectedNodes).map((node) => `'${node.name}'`).join(", "),
      }),
      okText: t("common.yes"),
      okType: "danger",
      cancelText: t("common.cancel"),
      onOk: async () => {
        await Promise.all((nodesToDelete ?? selectedNodes).map((node) => deleteDocumentNode.mutateAsync(node.id)));
        setSelectedNodes([]);
      },
    });
  };

  const handleFilesUpload = (_: RcFile, fileList: RcFile[]) => {
    if (fileList.length > 0) {
      const formData = new FormData();
      const parentId = findNodeByTreePath(fullTree, currentPath)?.id;

      if (parentId) {
        formData.append("parentNodeId", parentId);
      }

      formData.append("type", currentNodeType);
      fileList.forEach((file) => formData.append("files", file));
      fileList.splice(0, fileList.length);

      uploadDocumentNodes.mutate(formData);
    }

    return false;
  };

  const handleNodeOpen = (node?: DocumentNodeTree) => {
    setSelectedNodes([]);
    if (!node) {
      if (!currentPath) return;

      const lastSeparator = currentPath.lastIndexOf("|");
      setCurrentPath(lastSeparator === -1 ? "" : currentPath.slice(0, lastSeparator));
      return;
    }

    setCurrentPath(node.treePath);
  };

  return (
    <ContentWrapper>
      <Card title={<h2>{t("documentNode.titles.list")}</h2>} className="card-box">
        <DocumentNodeToolbarMenuView
          hasPermission={hasActionsPermission}
          anyNodeSelected={selectedNodes.length > 0}
          onFilesUpload={handleFilesUpload}
          onMoveNodesClick={handleMoveNodesClick}
          onCreateFolderClick={handleCreateFolderClick}
          onDeleteNodesClick={handleDeleteNodesClick}
        />
        <div className={styles.documentsMainWrapper}>
          <div className={styles.documentsNavigation}>
            <span className={styles.documentsTabsTitle}>{t("documentNode.navigation.title")}</span>

            <DocumentNodeBreadcrumbsView
              breadcrumbs={getBreadcrumbs(fullTree, currentPath)}
              treeNodeType={currentNodeType}
              onClickBreadcrumb={setCurrentPath}
            />
          </div>

          <Tabs
            tabPlacement="start"
            renderTabBar={(props) => (
              <div className={styles.tabs}>
                {Object.values(DocumentNodeType).map((type) => (
                  <div
                    className={classNames(styles.tab, props.activeKey === type ? styles.tabsActive : "")}
                    key={type}
                    onClick={(event) => props.onTabClick(type, event)}
                  >
                    {t("documentNode.enums.documentNodeType." + type)}
                  </div>
                ))}
              </div>
            )}
            onChange={(item) => handleTabKeyChange(item as DocumentNodeType)}
            items={Object.values(DocumentNodeType).map((type) => ({
              key: type,
              label: t("documentNode.enums.documentNodeType." + type),
              children: (
                <div style={{ paddingRight: 24 }}>
                  <DocumentNodeListView
                    hasPermission={hasActionsPermission}
                    fullTree={fullTree}
                    currentPath={currentPath}
                    selectedNodes={selectedNodes}
                    onNodesSelect={setSelectedNodes}
                    onNodeOpen={handleNodeOpen}
                    onFileDownload={(node) => downloadDocumentNode.mutate({ id: node.id })}
                    onRenameNodeClick={handleRenameNodeClick}
                    onMoveNodesClick={handleMoveNodesClick}
                    onDeleteNodesClick={handleDeleteNodesClick}
                  />

                  {hasActionsPermission ? (
                    <div className="margin-top-large margin-bottom-small margin-left-small margin-right-small">
                      <UploadDragger multiple showUploadList={false} beforeUpload={handleFilesUpload} />
                    </div>
                  ) : undefined}
                </div>
              ),
            }))}
          />
        </div>
      </Card>

      <DocumentNodeMoveForm
        isOpen={isOpenMoveModal}
        onFormCancel={() => {
          setIsOpenMoveModal(false);
          setNodesToMove(undefined);
          setSelectedNodes([]);
        }}
        nodesToMove={nodesToMove}
        dataNodes={[
          {
            title: t("documentNode.enums.documentNodeType." + currentNodeType),
            key: currentNodeType,
            children: getNodeFoldersTree(fullTree, nodesToMove ?? selectedNodes),
          },
        ]}
      />

      <DocumentNodeNameModalForm
        isOpen={isOpenRenameModal}
        nodeToRename={renameNode}
        type={renameNodeType}
        currentNode={findNodeByTreePath(fullTree, currentPath)}
        onFormCancel={() => {
          setIsOpenRenameModal(false);
          setRenameNode(undefined);
          setRenameNodeType("createFolder");
        }}
      />
    </ContentWrapper>
  );
};
